/**
 * The complete dictionary, and the fallback every other locale resolves into.
 *
 * ## Why keys are namespaced strings rather than nested objects
 *
 * A flat map with dotted keys keeps `TranslationKey` a plain string union, so
 * `t('nav.support')` is checked at compile time and a typo is a build error
 * rather than an English string leaking into a French page. Nested objects
 * give prettier source and worse guarantees.
 *
 * ## What belongs here
 *
 * Shell chrome — header, menu, language modal, footer, section furniture. Not
 * editorial content: article titles and service descriptions live in
 * `content/homepage-content.ts` and will come from a CMS with its own
 * translation workflow. Copying them here would fork the same sentence into
 * two systems, and the copy in the losing system goes stale silently.
 */

export const enGB = {
  // --- common -------------------------------------------------------------
  'common.close': 'Close',
  'common.search': 'Search',
  'common.viewAll': 'View all',
  'common.comingSoon': 'coming soon',
  'common.region': 'Region',
  'common.language': 'Language',

  // --- navigation ---------------------------------------------------------
  'nav.home': 'Just Treated — home',
  'nav.support': 'Support',
  'nav.signUp': 'Sign up',
  'nav.account': 'Your account',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.menuTitle': 'Everything on Just Treated',
  'nav.changeRegion': 'Change region and language',

  // --- menu groups --------------------------------------------------------
  'menu.services': 'Services',
  'menu.business': 'Business',
  'menu.pharmacy': 'Pharmacy',
  'menu.company': 'Company',
  'menu.help': 'Help',
  'menu.shop': 'Shop',
  'menu.actionsTitle': 'Grow with Just Treated',
  'menu.actionsLede': 'Bring your pharmacy, practice or fleet onto the platform.',

  // --- language modal -----------------------------------------------------
  'locale.title': 'Choose your region and language',
  'locale.searchLabel': 'Search region',
  'locale.searchPlaceholder': 'Search for a country or region',
  'locale.noResults': 'No regions match that search.',
  'locale.current': 'Selected',
  'locale.note':
    'Just Treated is written in English today. More languages will appear here as they are translated — none is listed before it exists.',

  // --- news ---------------------------------------------------------------
  'news.title': 'News and updates',

  // --- footer -------------------------------------------------------------
  'footer.blurb': 'Your trusted healthcare platform for medicines, consultations, lab tests and more.',
  'footer.navLabel': 'Footer',
  'footer.legalLine': '© {year} Just Treated. All rights reserved. GPhC-registered providers.',
  'footer.regionLabel': 'United Kingdom · English',
  'footer.social': 'Follow Just Treated',
} as const;

/** Every key the application may ask for. */
export type TranslationKey = keyof typeof enGB;

/**
 * A dictionary for any locale other than the fallback.
 *
 * Partial by design: a locale supplies only what genuinely differs, and
 * anything it omits resolves through `FALLBACK_OF`. Requiring completeness
 * would force translators to restate identical strings, and restated strings
 * drift.
 */
export type PartialDictionary = Partial<Record<TranslationKey, string>>;
